/**
 * Назначения из ожидаемого прихода.
 *
 * Три состояния денег, которые нельзя смешивать:
 *
 *   1. **деньги существуют** — лежат на счёте;
 *   2. **существующие деньги закреплены** — конверт под требование;
 *   3. **деньги назначены из ожидаемого прихода** — их ещё нет.
 *
 * Третье — план, а не покрытие. Платёж, на который назначены будущие деньги,
 * не становится ни обеспеченным, ни оплаченным: зарплата может прийти позже
 * срока, прийти меньше или не прийти вовсе. Пока приложение путало эти
 * состояния, человек видел «всё в порядке» там, где всё держалось на обещании.
 *
 * Отсюда два правила, которые здесь и живут:
 *
 * · **Поздние деньги не покрывают ранний срок.** Приход 10-го не закрывает
 *   платёж 9-го, как бы ни совпадали суммы.
 * · **Один приход нельзя обещать дважды.** Сумма назначений с одного прихода
 *   не может превышать его самого, и превышение называется поимённо.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { isAfter, nextOccurrence, type PlainDate } from './date.js';
import { monthOf } from './analytics.js';
import { conservativeIncome } from './available.js';
import {
  expectedRest, isAwaited, nextForStream, type IncomeFact,
} from './income.js';
import type { PaymentInstance } from './instances.js';
import type { IncomeExpectation, IncomeStream } from './types.js';

/** Назначение будущих денег на конкретное требование. */
/**
 * Откуда придут назначенные деньги.
 *
 * Объединение, а не пара необязательных полей: у назначения ровно один
 * источник, и запись «есть и то и это» не должна быть выразима. Раньше
 * источником могло быть только наступление регулярного дохода, и гонорар,
 * которого человек ждёт один раз, назначить на платёж было нельзя — при том
 * что именно им такой платёж обычно и закрывают.
 */
export type FutureMoney =
  /** Наступление регулярного дохода: источник и дата. */
  | { kind: 'stream_occurrence'; incomeStreamId: string; expectedDate: PlainDate }
  /** Конкретное ожидание, названное человеком. */
  | { kind: 'expectation'; expectationId: string };

/** Ключ источника: по нему считается, сколько от него уже обещано. */
export const futureMoneyKey = (m: FutureMoney): string => (
  m.kind === 'expectation'
    ? `expectation:${m.expectationId}`
    : `stream:${m.incomeStreamId}:${m.expectedDate}`
);

export interface PlannedAllocation {
  id: string;
  /**
   * Источник денег. Появился вместе с назначением от разового ожидания;
   * у старых записей его нет, и тогда источник читается из пары полей ниже
   * (`sourceOf`).
   */
  source?: FutureMoney;
  /** Откуда придут деньги. **Устарело**: читается только через `sourceOf`. */
  incomeStreamId: string;
  /** Когда их ждут. Назначение живёт этой датой, а не датой создания. */
  expectedDate: PlainDate;
  /** Сколько назначено. */
  amount: Money;
  /**
   * На что: наступление платежа, платёж по кредитке к дате либо цель.
   * Кредитка — с 03.11.2026: нехватку для беспроцентного периода тоже
   * закрывают будущей зарплатой; на приходе деньги закрепляются конвертом карты.
   */
  target:
    | { kind: 'obligation'; instanceId: string; obligationId: string }
    | { kind: 'card'; accountId: string; dueDate: PlainDate }
    | { kind: 'goal'; goalId: string };
  createdAt: string;
  /**
   * Что с назначением стало.
   *
   * `planned` — деньги ещё ждут; `done` — приход случился и назначение
   * исполнено настоящим конвертом или взносом; `cancelled` — человек отменил.
   * Исполненное и отменённое в прогнозе не участвуют: первое уже стало
   * существующими деньгами и было бы посчитано дважды, второго попросту нет.
   */
  status?: 'planned' | 'done' | 'cancelled';
}

/** Назначение всё ещё ждёт денег. Прогноз считается только по таким. */
export const isPlanned = (a: PlannedAllocation): boolean =>
  (a.status ?? 'planned') === 'planned';

/** Источник назначения. Старая запись читается как наступление потока. */
export const sourceOf = (a: PlannedAllocation): FutureMoney => a.source
  ?? { kind: 'stream_occurrence', incomeStreamId: a.incomeStreamId, expectedDate: a.expectedDate };

/** Что известно про покрытие одного наступления платежа. */
export interface Coverage {
  instanceId: string;
  label: string;
  dueDate: PlainDate;
  amount: Money;
  /** Деньги, которые уже есть и закреплены. */
  secured: Money;
  /** Назначено из прихода, который успевает к сроку. */
  plannedInTime: Money;
  /** Назначено из прихода, который приходит **после** срока. */
  plannedLate: Money;
  /** Когда ожидается ближайший из назначенных приходов. */
  expectedBy: PlainDate | null;
  /** Не покрыто ничем. */
  gap: Money;
  /**
   * Что показать человеку одной строкой.
   *
   * `covered` — деньги есть и закреплены; `forecast` — хватит, если доход
   * придёт вовремя; `late` — назначенные деньги приходят после срока;
   * `gap` — не хватает.
   */
  state: 'covered' | 'forecast' | 'late' | 'gap';
}

/** Один приход, обещанный нескольким требованиям. */
export interface Overcommitment {
  incomeStreamId: string;
  expectedDate: PlainDate;
  /** Сколько ожидается. */
  expected: Money;
  /** Сколько уже обещано. */
  promised: Money;
  /** Кому обещано — поимённо, чтобы человек выбрал сам. */
  targets: { label: string; amount: Money }[];
}

export interface ForecastInput {
  instances: readonly PaymentInstance[];
  allocations: readonly PlannedAllocation[];
  /** Сколько ожидается от каждого прихода. Без оценки назначать не из чего. */
  expected: readonly ExpectedIncome[];
  labelOf?: (allocation: PlannedAllocation) => string;
}

/** Назначение, источника которого больше нет. */
export interface OrphanedAllocation {
  allocation: PlannedAllocation;
  /** Что осталось без покрытия из-за этого. */
  label: string;
  amount: Money;
}

export interface ForecastResult {
  coverage: Coverage[];
  /** Приходы, обещанные больше одного раза. */
  overcommitted: Overcommitment[];
  /**
   * Назначения, чей источник исчез: ожидание отменено, пропущено или уже
   * пришло. Деньги, обещанные из ниоткуда, покрытием не считаются — но и
   * молча пропасть не должны: человек назначал их осознанно.
   */
  orphaned: OrphanedAllocation[];
}

/**
 * Что из чего покрыто и чего не хватает.
 *
 * Считается по наступлениям платежей, а не по обязательствам: у недельного
 * платежа их четыре в месяц, и назначение относится к одному.
 */
export function forecastCoverage(input: ForecastInput): ForecastResult {
  const coverage: Coverage[] = [];
  /*
    Назначение живёт, пока жив его источник.

    Отменённое или пропущенное ожидание перестаёт быть деньгами, и назначенное
    из него покрытием быть не может. Раньше проверки не было вовсе: отмена
    ожидания оставляла платёж «обеспеченным по прогнозу» деньгами, которых
    никто больше не ждёт.
  */
  const sources = new Set(input.expected.map((e) => futureMoneyKey(
    e.expectationId
      ? { kind: 'expectation', expectationId: e.expectationId }
      : {
        kind: 'stream_occurrence',
        incomeStreamId: e.incomeStreamId ?? '',
        expectedDate: e.expectedDate,
      },
  )));
  const planned = input.allocations.filter(isPlanned);
  const live = planned.filter((a) => sources.has(futureMoneyKey(sourceOf(a))));
  const orphaned: OrphanedAllocation[] = planned
    .filter((a) => !sources.has(futureMoneyKey(sourceOf(a))))
    .map((a) => ({
      allocation: a,
      label: input.labelOf?.(a) ?? 'Назначение',
      amount: a.amount,
    }));

  for (const inst of input.instances) {
    const mine = live.filter(
      (a) => a.target.kind === 'obligation' && a.target.instanceId === inst.id,
    );
    // Поздние деньги не покрывают ранний срок: приход 10-го не закрывает
    // платёж 9-го, как бы ни совпадали суммы.
    const inTime = mine.filter((a) => !isAfter(a.expectedDate, inst.scheduledDate));
    const late = mine.filter((a) => isAfter(a.expectedDate, inst.scheduledDate));

    const plannedInTime = add(...inTime.map((a) => a.amount));
    const plannedLate = add(...late.map((a) => a.amount));
    const need = clampPos(sub(inst.unpaid, inst.secured));
    const gap = clampPos(sub(need, plannedInTime));

    const dates = inTime.map((a) => a.expectedDate).sort();
    coverage.push({
      instanceId: inst.id,
      label: inst.label,
      dueDate: inst.scheduledDate,
      amount: inst.amount,
      secured: inst.secured,
      plannedInTime,
      plannedLate,
      expectedBy: dates.length > 0 ? dates[dates.length - 1]! : null,
      gap,
      state: need === ZERO
        ? 'covered'
        : gap > ZERO
          ? (plannedLate > ZERO ? 'late' : 'gap')
          : 'forecast',
    });
  }

  return {
    coverage,
    overcommitted: overcommitments({ ...input, allocations: live }),
    orphaned,
  };
}

/** Одни и те же будущие деньги, обещанные нескольким требованиям. */
function overcommitments(input: ForecastInput): Overcommitment[] {
  const out: Overcommitment[] = [];

  for (const income of input.expected) {
    // Ключ источника, а не пара полей: разовое ожидание тоже нельзя обещать
    // дважды, а `incomeStreamId` у него пуст.
    const key = futureMoneyKey(income.expectationId
      ? { kind: 'expectation', expectationId: income.expectationId }
      : {
        kind: 'stream_occurrence',
        incomeStreamId: income.incomeStreamId ?? '',
        expectedDate: income.expectedDate,
      });
    const mine = input.allocations.filter((a) => futureMoneyKey(sourceOf(a)) === key);
    const promised = add(...mine.map((a) => a.amount));
    if (promised <= income.amount) continue;

    out.push({
      incomeStreamId: income.incomeStreamId ?? '',
      expectedDate: income.expectedDate,
      expected: income.amount,
      promised,
      targets: mine.map((a) => ({
        label: input.labelOf?.(a) ?? a.target.kind,
        amount: a.amount,
      })),
    });
  }

  return out;
}

/**
 * Сколько ещё можно назначить с этого прихода.
 *
 * Ноль означает, что приход уже обещан целиком: назначать сверх него — то же
 * самое, что потратить одни деньги дважды.
 */
export function freeFromIncome(
  allocations: readonly PlannedAllocation[],
  income: { source: FutureMoney; amount: Money },
): Money {
  const key = futureMoneyKey(income.source);
  const promised = add(...allocations
    .filter(isPlanned)
    .filter((a) => futureMoneyKey(sourceOf(a)) === key)
    .map((a) => a.amount));
  return clampPos(sub(income.amount, promised));
}

/**
 * Назначения, которые исполнились приходом денег.
 *
 * Когда приход записан фактом, назначения с этой даты перестают быть планом:
 * деньги существуют, и дальше ими распоряжаются как существующими. Оставить
 * их планом значило бы посчитать один приход дважды — как ожидаемый и как
 * пришедший.
 */
export const fulfilledBy = (
  allocations: readonly PlannedAllocation[],
  incomeStreamId: string,
  date: PlainDate,
): PlannedAllocation[] => allocations.filter(
  (a) => isPlanned(a) && a.incomeStreamId === incomeStreamId && !isAfter(a.expectedDate, date),
);

/** Ожидаемый приход: то, из чего можно назначать. */
export interface ExpectedIncome {
  /** Источник, если приход регулярный. У разовых его нет. */
  incomeStreamId?: string;
  /** Запись ожидания, если приход назвал человек. */
  expectationId?: string;
  name: string;
  expectedDate: PlainDate;
  /** Осторожная оценка: у плавающего дохода берём нижнюю границу. */
  amount: Money;
  accountId: string;
  /**
   * Это остаток частичного прихода, а не весь доход.
   *
   * Половина зарплаты пришла, и человек сказал, что ждёт вторую. Обещать
   * целую зарплату после этого нельзя — её уже не будет; выбросить ожидание
   * тоже нельзя — деньги обещаны и на них рассчитывают.
   */
  partial?: boolean;
}

/** Уже записанный приход: чем он закрывает ожидание. */
export interface ReceivedIncome {
  incomeStreamId: string;
  date: PlainDate;
  /**
   * Сколько ещё ждут по этому приходу.
   *
   * `undefined` или ноль — приход состоялся целиком, ожидание закрыто.
   * Положительное число — пришла часть, и человек подтвердил, что ждёт
   * остаток. Догадываться об этом по одной лишь сумме нельзя: доход бывает
   * меньше просто потому, что месяц выдался такой.
   */
  rest?: Money;
  /** Когда записан приход — по этому времени выбирается последний ответ об остатке. */
  at?: string;
}

/**
 * Ближайшие ожидаемые приходы — одной моделью.
 *
 * Источник истины здесь один. Раньше прогноз собирал свои ожидания сам, из
 * `dayOfMonth`, пока рядом жила отдельная запись `IncomeExpectation` с
 * ритмом, статусами и частичным приходом. Два списка расходились: покрытие
 * платежа считалось по одному, а «Ждём приход» на главной — по другому, и
 * один и тот же гонорар мог оказаться и там и там.
 *
 * Теперь ожидания складываются из двух частей и **склеиваются по ключу**
 * «источник + дата»:
 *
 * · наступления регулярных источников — считаются из ритма;
 * · записанные ожидания (`planned`/`partial`) — берутся как есть, с остатком
 *   вместо полной суммы.
 *
 * Записанное ожидание всегда старше расчётного: в нём учтено то, что уже
 * пришло. Приход, закрытый фактом, из прогноза уходит — иначе одни и те же
 * деньги обещаны дважды.
 */
export function expectedIncomes(input: {
  streams: readonly IncomeStream[];
  today: PlainDate;
  received?: readonly ReceivedIncome[];
  /** Записанные ожидания: регулярные наступления и разовые приходы. */
  expectations?: readonly IncomeExpectation[];
  /** Приходы, которые случились: по ним видно, что наступление закрыто. */
  facts?: readonly IncomeFact[];
}): ExpectedIncome[] {
  const received = input.received ?? [];
  const out: ExpectedIncome[] = [];
  /** Ключ наступления: источник и дата. */
  const key = (streamId: string | undefined, date: PlainDate): string => `${streamId ?? '-'}:${date}`;
  const taken = new Set<string>();

  // 1. Записанные ожидания. Они знают о частичном приходе, поэтому идут
  //    первыми и закрывают свой ключ для расчётных наступлений.
  for (const e of input.expectations ?? []) {
    if (!isAwaited(e)) continue;
    const rest = expectedRest(e);
    if (rest <= ZERO) continue;
    taken.add(key(e.streamId, e.expectedDate));
    out.push({
      ...(e.streamId ? { incomeStreamId: e.streamId } : {}),
      expectationId: e.id,
      name: e.name,
      expectedDate: e.expectedDate,
      amount: rest,
      accountId: e.accountId,
      ...(e.status === 'partial' ? { partial: true } : {}),
    });
  }

  // 2. Наступления по ритму — то, чего человек ещё не записывал.
  for (const s of input.streams) {
    const expectedDate = nextForStream(s, input.today);
    if (!expectedDate) continue;
    if (taken.has(key(s.id, expectedDate))) continue;

    // Факт прихода по этому наступлению закрывает его целиком: остаток
    // человек называет сам, и тогда он живёт записанным ожиданием.
    const closed = (input.facts ?? []).some(
      (f) => f.kind === 'regular' && f.streamId === s.id && monthOf(f.date) === monthOf(expectedDate),
    );
    const factsOf = received.filter(
      (r) => r.incomeStreamId === s.id && monthOf(r.date) === monthOf(expectedDate),
    );

    if (factsOf.length === 0 && !closed) {
      out.push({
        incomeStreamId: s.id, name: s.name, expectedDate,
        amount: conservativeIncome(s), accountId: s.accountId,
      });
      continue;
    }

    // Приход был. Остаётся ровно то, что человек назвал остатком, — и ничего,
    // если он сказал, что больше не ждёт.
    // Остаток — по последнему ответу человека, а не сумма ответов по частям:
    // «жду 50 000», потом вторая часть 20 000 с «жду 30 000» — ждём 30 000,
    // а не 80 000.
    const answered = factsOf
      .filter((f) => f.rest !== undefined)
      .sort((a, b) => (a.at ?? a.date).localeCompare(b.at ?? b.date));
    const rest = answered.length > 0 ? answered[answered.length - 1]!.rest ?? ZERO : ZERO;
    if (rest > ZERO) {
      out.push({
        incomeStreamId: s.id, name: s.name, expectedDate,
        amount: rest, accountId: s.accountId, partial: true,
      });
    }
  }

  return out.sort((a, b) => a.expectedDate.localeCompare(b.expectedDate));
}
